// Hooks
import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

import {
    I_apiGetDeps_Brands,
    I_apiGetDeps_Brands_Categories,
    I_apiGetDeps_Categories,
} from "@/hooks/requests/apiGetDeps/apiGetDeps.types";
// Stores
import { useTIWStore } from "@/stores/tiw/tiw";
import apiGetModels from "~/tiw/hooks/requests/apiGetModels/apiGetModels";
import apiGetProducts from "~/tiw/hooks/requests/apiGetProducts/apiGetProducts";
import { I_apiGetProducts_Data_Arr } from "~/tiw/hooks/requests/apiGetProducts/apiGetProducts.types";

// Types
import {
    Fields,
    FieldsMap,
    hierarchy,
    type FieldName,
    type I_field_brand,
    type I_field_category,
    type I_field_model,
    type I_field_other,
} from "./chooseDevice.types";

function uniqueBy<T>(array: T[], key: keyof T) {
    return array.filter(
        (item, index, self) => index === self.findIndex((t) => t[key] === item[key]),
    );
}

/**
 * * 🆂🆃🅾🆁🅴 [Choose Device]
 * */
export const useChooseDeviceStore = defineStore("chooseDevice", () => {
    const TIWStore = useTIWStore();
    const isLoading = reactive({
        product: false,
        model: false,
    });
    const brands = ref<I_apiGetDeps_Brands[]>(null);
    const brandsCategories = ref<I_apiGetDeps_Brands_Categories[] | null>(null);
    const categories = ref<I_apiGetDeps_Categories[] | null>(null);
    const products = ref<I_apiGetProducts_Data_Arr | null>(null);

    const field_imei = reactive<{
        value: string;
        savedValue: string;
    }>({
        value: "",
        savedValue: "",
    });

    const field_serial = reactive<{
        value: string;
        savedValue: string;
    }>({
        value: "",
        savedValue: "",
    });

    const field_brand = reactive<I_field_brand>({
        value: null,
        data: [],
    });

    const field_category = reactive<I_field_category>({
        value: null,
        data: [],
    });

    const field_model = reactive<I_field_model>({
        value: null,
        data: [],
    });

    const field_storage = reactive<I_field_other>({
        value: null,
        data: [],
    });

    const field_color = reactive<I_field_other>({
        value: null,
        data: [],
    });

    const field_CPU = reactive<I_field_other>({
        value: null,
        data: [],
    });

    const field_GPU = reactive<I_field_other>({
        value: null,
        data: [],
    });
    const field_RAM = reactive<I_field_other>({
        value: null,
        data: [],
    });

    const field_Memory = reactive<I_field_other>({
        value: null,
        data: [],
    });

    const fieldsMap: FieldsMap = {
        field_brand,
        field_category,
        field_model,
        field_storage,
        field_color,
        field_CPU,
        field_GPU,
        field_RAM,
        field_Memory,
    };

    function resetBelow(field: FieldName) {
        const startIndex = hierarchy.indexOf(field);
        if (startIndex === -1) return;

        for (let i = startIndex + 1; i < hierarchy.length; i++) {
            fieldsMap[hierarchy[i]].value = null;
            fieldsMap[hierarchy[i]].data = [];
        }
    }

    watch(
        () => field_brand.value,
        (val) => {
            resetBelow("field_brand");

            if (!val) return;

            const categoriesFromBrand = brandsCategories.value
                .filter((el) => el.brand_id === field_brand.value.brand_id)
                .map((el) => el.category_id);
            const foundCategories = categories.value.filter((el) =>
                categoriesFromBrand.includes(el.category_id),
            );

            if (
                field_brand.value.brand_id === "170161" ||
                field_brand.value.name.toLocaleLowerCase() === "apple"
            ) {
                foundCategories.sort((a, b) => {
                    if (a.name.toLocaleLowerCase() === "iphone") return -1;
                    if (b.name.toLocaleLowerCase() === "iphone") return 1;
                    return 0;
                });
            }

            if (foundCategories.length === 1) field_category.value = foundCategories[0];
            field_category.data = foundCategories;
        },
    );

    watch(
        () => field_category.value,
        async (val) => {
            resetBelow("field_category");

            if (!val) return;

            isLoading.model = true;
            const foundModels = (
                await apiGetModels({
                    brand_id: field_brand.value.brand_id,
                    category_id: field_category.value.category_id,
                    token: TIWStore.WIDGET_DATA.token,
                })
            ).data;
            isLoading.model = false;

            field_model.data = foundModels;
        },
        { deep: true },
    );

    watch(
        () => field_model.value,
        async (val) => {
            resetBelow("field_model");

            if (!val) return;

            isLoading.product = true;
            const foundProducts = (
                await apiGetProducts({
                    model_id: field_model.value.model_id,
                    token: TIWStore.WIDGET_DATA.token,
                })
            ).data;
            isLoading.product = false;

            products.value = foundProducts;

            field_storage.data = uniqueBy(foundProducts, "storage");

            field_CPU.data = uniqueBy(foundProducts, "keyShow2");
        },
    );

    watch(
        () => field_CPU.value,
        (val) => {
            resetBelow("field_CPU");

            if (!val) return;

            const foundGPU = products.value.filter((el) => el.keyShow2 === val.keyShow2);
            const uniqueGPU = uniqueBy(foundGPU, "keyShow3");

            field_GPU.data = uniqueGPU;
        },
    );

    watch(
        () => field_GPU.value,
        (val) => {
            resetBelow("field_GPU");
            if (!val) return;

            const foundRAM = products.value.filter((el) => {
                return el.keyShow2 === val.keyShow2 && el.keyShow3 === val.keyShow3;
            });

            const uniqueRAM = uniqueBy(foundRAM, "keyShow4");

            field_RAM.data = uniqueRAM;
        },
    );

    watch(
        () => field_RAM.value,
        (val) => {
            resetBelow("field_RAM");

            if (!val) return;

            const foundMemory = products.value.filter((el) => {
                return (
                    el.keyShow2 === val.keyShow2 &&
                    el.keyShow3 === val.keyShow3 &&
                    el.keyShow4 === val.keyShow4
                );
            });

            const uniqueMemory = uniqueBy(foundMemory, "keyShow5");

            field_Memory.data = uniqueMemory;
        },
    );

    watch(
        () => field_storage.value,
        (val) => {
            resetBelow("field_storage");

            if (!val) return;

            const foundColors = products.value.filter(
                (el) => el.storage === field_storage.value.storage,
            );
            field_color.data = foundColors;
        },
    );

    /** Изображение "Превью" выбранного устройства для следующего шага формы */
    const deviceImagePreview = ref<string | null>(null);

    const crazyInit = ref<boolean>(false);

    const isCommercialNotebook = computed(() => {
        return (
            field_category.value?.name === "PC Notebook Commercial" ||
            field_category.value?.category_id === "367"
        );
    });

    const permanentIgnore = computed<boolean>((): boolean => {
        // category "Mac"
        if (field_category.value?.category_id === "3") {
            return true;
        }

        return false;
    });

    const brandName = computed<string>((): string => {
        if (field_brand.value.name) {
            return field_brand.value.name;
        }

        return "";
    });

    const code = computed(() => {
        if (isCommercialNotebook.value) {
            return field_Memory.value.productId;
        } else {
            return field_color.value.productId;
        }
    });

    const isImei = computed<boolean>(() => {
        return (
            field_category.value.name.toLowerCase() === "iphone" ||
            field_category.value.name.toLowerCase() === "smartphone"
        );
    });

    const isAcceptedNext = computed<boolean>(() => {
        const requiredFields: Fields[] = [field_brand, field_category, field_model];

        if (isCommercialNotebook.value) {
            requiredFields.push(field_CPU, field_GPU, field_RAM, field_Memory);
        } else {
            requiredFields.push(field_color, field_storage);
        }

        return requiredFields.every((field) => Boolean(field.value));
    });

    /**
     * Цепочка характеристик устройства
     * @example ["Apple", "iPhone", "12 Pro Max", "256GB", "Graphite"]
     */
    const deviceChain = computed<string[]>(() => {
        return [
            field_brand.value?.name,
            field_category.value?.name,
            field_model.value?.name,
            field_storage.value?.storage,
            field_color?.value?.color,
            field_CPU?.value?.keyShow2,
            field_GPU?.value?.keyShow3,
            field_RAM?.value?.keyShow4,
            field_Memory?.value?.keyShow5,
        ].filter((item: string | undefined) => item);
    });

    const restoreToDefault = () => {
        [field_category, field_model, field_storage, field_color].forEach((field) => {
            field.value = null;
            field.data = [];
        });

        field_imei.value = "";
        field_imei.savedValue = "";
        field_brand.value = null;
        deviceImagePreview.value = null;
    };

    return {
        // * 🆂🆃🅾🆁🅴
        field_imei,
        field_brand,
        field_category,
        field_model,
        field_storage,
        field_color,
        field_serial,
        brands,
        categories,
        brandsCategories,
        deviceImagePreview,
        crazyInit,
        isLoading,
        field_CPU,
        field_GPU,
        field_RAM,
        field_Memory,

        // * 🅲🅾🅼🅿🆄🆃🅴🅳
        isImei,
        permanentIgnore,
        brandName,
        isAcceptedNext,
        deviceChain,
        isCommercialNotebook,
        code,

        // * 🅼🅴🆃🅷🅾🅳🆂
        restoreToDefault,
    };
});
